import { MotionValue, motion } from "framer-motion";
import { MouseEventHandler } from "react";

import { TableColumnType } from "@/components/@types";
import { ResolvedGridAppearance } from "@/components/organisms/Table/Table.tools";
import { useTableStore } from "@/components/organisms/Table/Table.store";
import { TableHeaderCell } from "@/components/organisms/Table/TableHeaderCell";
import { TableHeaderCheckbox } from "@/components/organisms/Table/TableHeaderCheckbox";
import { Values, ZIndex } from "@/constants";
import { sanitizeKeyForCSS } from "@/hooks/useContextTree";

export function TableHeaderRow({
  getClassName,
  handleMouseDown,
  hideOuterBorders = false,
  hideCheckboxes = false,
  lockedColumns,
  negativeScrollLeft,
  objectKeyToIndex,
  rowHeight,
  unlockedColumns,
  gridColors,
  totalCount,
  isLoading = false,
}: {
  getClassName: (objectKey: string) => string;
  handleMouseDown: (objectKey: string) => MouseEventHandler<HTMLDivElement>;
  hideOuterBorders?: boolean;
  hideCheckboxes?: boolean;
  lockedColumns: TableColumnType[];
  negativeScrollLeft: MotionValue<number>;
  objectKeyToIndex: Record<string, number>;
  rowHeight: number;
  unlockedColumns: TableColumnType[];
  gridColors: ResolvedGridAppearance;
  totalCount: number;
  isLoading?: boolean;
}) {
  // Extract contextId from getClassName output for CSS variable construction
  // getClassName returns `${contextId}-column-${sanitizedKey}`
  const extractContextId = (className: string): string => {
    const parts = className.split("-column-");
    return parts[0] || "";
  };
  // Get contextId from first column's class name (all have same contextId)
  const sampleClassName =
    lockedColumns[0]?.objectKey || unlockedColumns[0]?.objectKey || "";
  const contextId = sampleClassName
    ? extractContextId(getClassName(sampleClassName))
    : "";
  const layoutIndexToColumnIndex = useTableStore(
    (state) => state.layoutIndexToColumnIndex,
  );

  return (
    <>
      {!hideCheckboxes && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: ZIndex.GRID.HEADER_CHECKBOX,
            borderRight: "1px solid",
            borderBottom: "1px solid",
            borderColor: "var(--border)",
            width: Values.DEFAULT_ROW_HEIGHT + "px",
            minWidth: Values.DEFAULT_ROW_HEIGHT + "px",
            maxWidth: Values.DEFAULT_ROW_HEIGHT + "px",
            height: rowHeight,
            minHeight: rowHeight,
            maxHeight: rowHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: gridColors.headerBackground,
          }}
        >
          <TableHeaderCheckbox totalCount={totalCount} isLoading={isLoading} />
        </div>
      )}
      {/* Locked headers */}
      {(() => {
        return lockedColumns.map((col, colIdx) => {
          const actualColIdx = objectKeyToIndex[col.objectKey] || 0;
          const layoutIndex = layoutIndexToColumnIndex.findIndex(
            (storedColIdx) => storedColIdx === actualColIdx,
          );
          const sanitizedKey = sanitizeKeyForCSS(col.objectKey);
          const headerClassName = getClassName(col.objectKey);
          const offsetVar = `--${contextId}-column-${sanitizedKey}-offset`;
          const isLastLockedColumn =
            colIdx === lockedColumns.length - 1 && unlockedColumns.length === 0;

          const header = (
            <div
              key={`locked-header-${col.objectKey}`}
              className={`${headerClassName} ${contextId}-header-${col.objectKey}`}
              style={{
                position: "absolute",
                top: 0,
                left: `var(${offsetVar})`,
                height: rowHeight,
                backgroundColor: gridColors.headerBackground,
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                zIndex:
                  ZIndex.GRID.HEADER_LOCKED - lockedColumns.length - colIdx,
              }}
            >
              <TableHeaderCell
                column={col}
                index={actualColIdx}
                isLastColumn={isLastLockedColumn}
                hideOuterBorders={hideOuterBorders}
                layoutIndex={layoutIndex}
                getClassName={getClassName}
              />
              {col.isResizable && (
                <div
                  onMouseDown={handleMouseDown(col.objectKey)}
                  style={{
                    position: "absolute",
                    zIndex: ZIndex.GRID.COLUMN_RESIZE,
                    right: -0.5,
                    top: "50%",
                    transform: "translate(45%, -50%)",
                    width: 4,
                    height: 32,
                    borderRadius: "0.25rem",
                    backgroundColor: "transparent",
                    transition: "all 0.3s",
                    cursor: "col-resize",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "var(--primary)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                />
              )}
            </div>
          );

          return header;
        });
      })()}

      {/* Unlocked headers Container - positions relative to the table, scrolls with x */}
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          height: rowHeight,
          x: negativeScrollLeft,
          zIndex: ZIndex.GRID.HEADER_SCROLLABLE_CONTAINER,
        }}
      >
        {unlockedColumns.map((col, colIdx) => {
          const actualColIdx = objectKeyToIndex[col.objectKey] || 0;
          const layoutIndex = layoutIndexToColumnIndex.findIndex(
            (storedColIdx) => storedColIdx === actualColIdx,
          );
          const sanitizedKey = sanitizeKeyForCSS(col.objectKey);
          const headerClassName = getClassName(col.objectKey);
          const offsetVar = `--${contextId}-column-${sanitizedKey}-offset`;
          const isLastUnlockedColumn = colIdx === unlockedColumns.length - 1;

          return (
            <div
              key={`header-${col.objectKey}`}
              className={`${headerClassName} ${contextId}-header-${col.objectKey}`}
              style={{
                position: "absolute",
                top: 0,
                left: `var(${offsetVar})`,
                height: rowHeight,
                backgroundColor: gridColors.headerBackground,
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                zIndex:
                  ZIndex.GRID.HEADER_UNLOCKED - unlockedColumns.length - colIdx,
              }}
            >
              <TableHeaderCell
                column={col}
                index={actualColIdx}
                isLastColumn={isLastUnlockedColumn}
                hideOuterBorders={hideOuterBorders}
                layoutIndex={layoutIndex}
                getClassName={getClassName}
              />
              {col.isResizable && (
                <div
                  onMouseDown={handleMouseDown(col.objectKey)}
                  style={{
                    position: "absolute",
                    zIndex: ZIndex.GRID.COLUMN_RESIZE,
                    right: -0.5,
                    top: "50%",
                    transform: "translate(45%, -50%)",
                    width: 4,
                    height: 32,
                    borderRadius: "0.25rem",
                    backgroundColor: "transparent",
                    transition: "all 0.3s",
                    cursor: "col-resize",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "var(--primary)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                />
              )}
            </div>
          );
        })}
      </motion.div>
    </>
  );
}
